import React from 'react';
import { Formik } from 'formik';
import { makeStyles } from 'tss-react/mui';
import { useAlert } from '../../../../AlertProvider';

import { AppointmentRecurring, AppointmentSlotDuration } from '@uplift/api';
import { useCurrentuser, useTimezone } from '@uplift/contexts';
import { CalendarDateService } from '@uplift/services';
import { ProviderMedicalRole } from '@uplift/types';
import { useL10n } from '@uplift/hooks';

import { ModalTitle } from '../../../../Modal';
import { ModalFullPage, ModalFullPageActions, ModalFullPageContent } from '../../../../ModalFullPage';
import { AddSessionFormMobile } from './AddSessionFormMobile.component';
import { CreateAppointmentFormValues } from '../AddSessionForm.types';
import { useAddSessionValidationSchema } from '../AddSessionForm.validation';
import { AddSessionFormSubmitButton } from '../components/AddSessionFormSubmitButton';
import { useAddSession } from '@uplift/queries';
import { useQueryClient } from '@tanstack/react-query';
import { DateFormats, providerScheduleViewTypeRoute } from '@uplift/constants';


const useStyles = makeStyles()(theme => ({
  root: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    paddingLeft: 0,
    paddingRight: 0,
  },
}));


export type AddSessionFormFormikMobileProps = {
  onClose: () => void;
  onSuccess: () => Promise<void>;

  newEvent?: { start?: string };
  patientId?: string;
};


export const AddSessionFormikMobile: React.FC<AddSessionFormFormikMobileProps> = (props) => {
  const queryClient = useQueryClient();
  const l10n = useL10n();
  const timezone = useTimezone();
  const alert = useAlert();
  const me = useCurrentuser();
  const isPsychiatrist = me.medicalRole === ProviderMedicalRole.psychiatrist;
  const shouldInvitePatient = providerScheduleViewTypeRoute.useQueryParam('createAppointment') === 'invite';

  const startDate = props.newEvent?.start;
  const initialDateValue = React.useMemo(() => {
    if (startDate) {
      return CalendarDateService
        .date(startDate)
        .tz(timezone)
        .format(DateFormats.ISO_DATE);
    }

    return undefined;
  }, [ startDate, timezone ]);

  const validator = useAddSessionValidationSchema();
  const { addSession } = useAddSession();

  const onSubmit = async (values: CreateAppointmentFormValues, helpers: { setStatus: (e: any) => void }) => {
    helpers.setStatus({
      errorDisplayed: false,
      errorDescription: '',
      submissionBlocked: false,
    });

    try {
      const { patient } = await addSession(values);

      await props.onSuccess?.();
      queryClient.invalidateQueries([ 'calendar' ]);
      if (values.isNewPatient) {
        alert.success(l10n.schedule.createAppointment.invitationSent(patient.preferredName || patient.firstName));
      } else {
        if (values.repeats === AppointmentRecurring.none) {
          alert.success(l10n.schedule.createAppointment.sessionAdded);
        } else {
          alert.success(l10n.schedule.createAppointment.sessionsAdded);
        }
      }
    } catch (e) {
      if (e instanceof Error) {
        helpers.setStatus({
          errorDisplayed: true,
          errorDescription: e?.message || 'unknown',
          submissionBlocked: false,
        });
      } else {
        helpers.setStatus({
          errorDisplayed: true,
          errorDescription: 'unknown',
          submissionBlocked: false,
        });
      }

    }
  };

  const duration = React.useMemo(() => {
    if (isPsychiatrist) {
      return AppointmentSlotDuration.thirty;
    }
    return AppointmentSlotDuration.sixty;
  }, [ isPsychiatrist ]);

  return (
    <Formik
      initialValues={{
        isPsychiatrist: isPsychiatrist,
        isNewPatient: shouldInvitePatient ?? false,
        existingPatient: props.patientId ? {
          id: props.patientId,
          email: '',
          firstName: '',
          lastName: '',
        } : undefined,
        invitedPatient: {
          firstName: '',
          lastName: '',
          email: '',
          sex: undefined,
          birthDate: undefined
        },
        duration: duration,
        repeats: AppointmentRecurring.none,
        date: initialDateValue ?? null,
        startTime: startDate ?? null,
        notes: '',
      }}
      onSubmit={onSubmit}
      validationSchema={validator}
      initialStatus={{
        errorDisplayed: false,
        errorDescription: '',
        submissionBlocked: false,
      }}
    >
      {props.children}
    </Formik>
  );
};


export const AddSessionFormFormikMobile: React.FC<AddSessionFormFormikMobileProps & { open?: boolean }> = (props) => {
  const l10n = useL10n();
  const { classes } = useStyles();

  return (
    <AddSessionFormikMobile {...props}>
      <ModalFullPage open={props.open} transition mobileOnly keepMounted>
        <ModalTitle onClose={props.onClose}>
          {l10n.schedule.createAppointment.newSession}
        </ModalTitle>
        <ModalFullPageContent dividers classes={classes}>
          <AddSessionFormMobile
            open={props.open}
            newEvent={props.newEvent}
          />
        </ModalFullPageContent>
        <ModalFullPageActions>
          <AddSessionFormSubmitButton fullWidth size="medium" color="secondary">
            {l10n.general.save}
          </AddSessionFormSubmitButton>
        </ModalFullPageActions>
      </ModalFullPage>
    </AddSessionFormikMobile>
  );
};
